import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, CircularProgress, Link, Typography } from "@mui/material";
import { useState } from "react";

type Props = {
	/** When the browser received the list, as returned by React Query. */
	updatedAt?: number;
	onReload: () => Promise<unknown>;
	onError?: (message: string) => void;
};

/**
 * Footer for a catalog grid: when the list was last brought in, and a link to bring it again.
 * Reloading goes past the copy the API has stored, so it always ends up with what the database
 * says at that moment.
 */
export const CatalogFreshness = ({ updatedAt, onReload, onError }: Props) => {
	const [isReloading, setIsReloading] = useState(false);

	const handleReload = async () => {
		setIsReloading(true);
		try {
			await onReload();
		} catch (e: any) {
			onError?.(e?.message || "The list could not be reloaded");
		} finally {
			setIsReloading(false);
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.75,
				mt: 0.5,
				minHeight: 24,
			}}
		>
			<Typography variant="caption" color="text.secondary" lineHeight={1}>
				{updatedAt
					? `List updated at ${new Date(updatedAt).toLocaleTimeString()}`
					: "List not loaded yet"}
			</Typography>
			{isReloading ? (
				<CircularProgress size="0.75rem" />
			) : (
				<Link
					component="button"
					type="button"
					variant="caption"
					underline="hover"
					onClick={handleReload}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						gap: 0.25,
						// Both the icon and the label sit on the same line: MUI's Link keeps the
						// button's default line-height otherwise and the icon rides half a pixel high.
						lineHeight: 1,
						p: 0,
					}}
				>
					<RefreshIcon sx={{ fontSize: "0.9rem", display: "block" }} />
					Reload
				</Link>
			)}
		</Box>
	);
};
